import { Input, Form, Button } from 'antd'
import { useCoinBasis } from '@/hooks/coin'
import { toFixed, toThousands } from '@/utils/number'
import { memo, FC, useMemo, useEffect, useState } from 'react'
import { useTranslation } from '@/services/i18n'
import styles from './index.module.scss'
import { useValidateSendAmount, ValidatedAmountData } from '@/hooks/tx'
import { useCalcUsdValue } from '@/hooks/tx'
import { CURRENCY } from '@/constants'

interface SendNumberInputProps extends ValidatedAmountData {
  isShowMax: boolean
  onCallbackError?: (hasErr: boolean) => void
}

const SendNumberInput: FC<SendNumberInputProps> = props => {
  const { t } = useTranslation()
  const { coinKey, onCallbackError, value, accountInfo, isShowMax } = props
  const coinBasis = useCoinBasis(coinKey)
  const precision = coinBasis?.showCoinDecimal
  const [hasError, setHasError] = useState(false)

  // 处理过精度的余额
  const fixedBalance = useMemo(() => {
    return typeof precision === 'number'
      ? toFixed(accountInfo?.coinBalance, precision)
      : accountInfo?.coinBalance
  }, [accountInfo, precision])

  const { errMsg, sendAll, handleChangeText } = useValidateSendAmount(props)

  useEffect(() => {
    const hasError = !!(value && errMsg)
    setHasError(hasError)
    onCallbackError?.(hasError)
  }, [value, errMsg])

  const usdValue = useCalcUsdValue(value, coinBasis?.exchangeRateUsd)

  return (
    <Form.Item validateStatus={hasError ? 'error' : ''}>
      <div className={styles.label}>
        <span>{t('common.num')}</span>
        <div className="flex flex-row items-center">
          <div className={styles.balanceWrapper}>
            <span className={styles.balanceLabel}>{t('common.balance')}:</span>
            <span>
              {toThousands(fixedBalance || 0)} {coinBasis?.coinName}
            </span>
          </div>
          {isShowMax && (
            <Button type="link" className={styles.balanceMax} onClick={sendAll}>
              {t('common.max')}
            </Button>
          )}
        </div>
      </div>
      <Input
        value={value}
        onChange={evt => handleChangeText(evt.target.value)}
        className={styles.inputNumber}
        suffix={coinBasis?.coinName}
        placeholder={t('wallet.sendDrawer.sendAmountPlaceholder') || ''}
      />
      <div className={styles.explain}>
        <span className={styles.balanceLabel}>{`≈${usdValue} ${CURRENCY}`}</span>
        {hasError && <span className={styles.errMsg}>{errMsg}</span>}
      </div>
    </Form.Item>
  )
}

export default memo(SendNumberInput)
